// ONE rule for "go one step back", used by both back gestures:
//   • the Android hardware button (components/BackButtonHandler.jsx)
//   • Escape / Backspace on a keyboard (components/BackKeyHandler.jsx)
//
// One press = one step, and the steps are always the same:
//   1 something is OPEN (a form, a sheet, the menu drawer) → close it, stay put
//     (that part is formStack.closeTopForm, called before this);
//   2 we walked INTO this page inside the app → one history step back;
//   3 a sub-page opened cold (reload, a shared link) → its parent page;
//   4 anywhere else under /app → the MAIN page (mainPath below) - never back
//     into the sign-in screens behind it;
//   5 already on the main page → the app asks whether to exit (on a phone) or
//     does nothing (in a browser tab, where the tab is not ours to close).
//
// Keeping it here means the phone and the keyboard can never drift apart.

// Where the app opens, and therefore the floor of the back ladder. ONE rule,
// read by App.jsx <Home> and by both back handlers, so a back press can never
// land somewhere the app would not have opened on.
// Card distribution started on 2026-10-26: anyone with the guest list lands on
// their own round of cards. A vendor (no guest access) has no cards at all.
export const mainPath = ({ signedIn = false, isAdmin = false, canGuests = false } = {}) => {
  if (!signedIn) return '/';
  if (isAdmin || canGuests) return '/app/cards';
  return '/app/members';
};

// Pages that are a floor: there is nothing of ours behind them.
export const ROOTS = new Set(['/', '/guest', '/invite']);

// A sub-page reached without in-app history steps up to its parent first,
// never straight past it.
export const parentOf = (path) => {
  if (path === '/app/label-setup') return '/app/labels';
  if (/^\/app\/services\/[^/]+$/.test(path)) return '/app/services';
  return null;
};

// → -1 (one history step) · a path to go to · 'exit' · null (do nothing)
//   path   where we are now
//   depth  in-app steps we can still pop (counted from the router's PUSH/POP)
//   main   this user's main page (mainPath above; '/' when signed out)
//   canExit  true on the phone, where the main page may ask to leave the app
export function backTarget({ path, depth = 0, main = '/', canExit = false }) {
  if (depth > 0) return -1;
  const parent = parentOf(path);
  if (parent) return parent;
  if (path.startsWith('/app')) {
    if (path === main || path === '/app') return canExit ? 'exit' : null;
    return '/app';                      // the main page, via App.jsx <Home>
  }
  if (ROOTS.has(path)) return canExit ? 'exit' : null;
  return '/';                           // sign-up, forgot password, OTP → Sign in
}
